import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F163jSchdADataRefreshService } from './f163j-schd-a-data-refresh.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f163j-schd-a-data-refresh',
    templateUrl: './f163j-schd-a-data-refresh.component.html',
    styleUrls: ['./f163j-schd-a-data-refresh.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F163jSchdADataRefreshComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };

  // 🔴 CRITICAL: Row data from AngularJS - NO MOCKS!
  rowData: any = {};

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};

  // Component Properties
  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  workspaceData: any[] = [];
  
  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  
  title: string = "Confirm";
  activeScreenData: any[] = [];
  
  // Loading states
  loading: boolean = false;
  processing: boolean = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f163jSchdADataRefreshService: F163jSchdADataRefreshService,
    private _snackBar: MatSnackBar,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    const d = activeScreen?.data;
    if (Array.isArray(d?.data)) {
      this.workspaceData = d.data;
    } else if (Array.isArray(d)) {
      this.workspaceData = d;
    } else {
      this.workspaceData = [];
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }
      
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // Setup active screen data from workspaceData
      this.setupActiveScreenData();
    } catch (e) {
      console.error('Error in F163jSchdADataRefresh init:', e);
    }
  }

  private setupActiveScreenData(): void {
    // Use workspaceData from globalService (similar to workspaceFactory.activeScreen.data.data)
    this.activeScreenData = this.workspaceData || [];
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  refreshData() {
    if (this.processing) {
      this._snackBar.open("Please wait while we save this request", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.processing = true;
    this.loading = true;

    try {
      // Build the JSON array object similar to original AngularJS logic
      let json_arrOBJ: any[] = [];
      
      if (this.activeScreenData && this.activeScreenData.length > 0) {
        this.activeScreenData.forEach((i: any) => {
          let ar = [
            {"amount": i.LN_43C, "acct_key": i['LN_43C_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43D, "acct_key": i['LN_43D_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43F, "acct_key": i['LN_43F_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43G, "acct_key": i['LN_43G_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43E, "acct_key": i['LN_43E_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43H, "acct_key": i['LN_43H_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY},
            {"amount": i.LN_43I, "acct_key": i['LN_43I_TO_ACCT'], "pship_combination_key": i.PSHIP_COMBINATION_KEY, "ptr_combination_key": i.PTR_COMBINATION_KEY}
          ];
          json_arrOBJ.push(ar);
        });
      }

      // Call service to save the data
      this.f163jSchdADataRefreshService.refreshSchdAData(
        this.GENERAL_CONFIG.base_url,
        json_arrOBJ,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.processing = false;
          this.loading = false;

          if (data.callSuccess === "1") {
            this._snackBar.open("Data saved Successfully.", 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.requestGridUpdate.emit('refresh');
          } else {
            this._snackBar.open(data.errorMessage || 'An error occurred during processing.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.requestGridUpdate.emit('cancel');
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.processing = false;
          this.loading = false;
          this._snackBar.open('Error occurred while processing. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.requestGridUpdate.emit('cancel');
          this.cdr.markForCheck();
        }
      );
    } catch (e) {
      this.processing = false;
      this.loading = false;
      this._snackBar.open('Error occurred while processing data. Please try again.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.requestGridUpdate.emit('cancel');
    }
  }
}
